// FILE: src/components/ManagedAccounts.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  collection, doc, getDoc, getDocs, query, where, Timestamp,
} from "firebase/firestore";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";

import PageScaffold from "./ui/PageScaffold";
import VideoLightbox from "./ui/VideoLightbox";

/* ----- small util for ?debug=1 ----- */
function useQueryFlag(name) {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name);
}

/* ---------- helpers ---------- */
function virtualGrantFromInvite(inv) {
  const g = {
    ownerUid: inv?.ownerUid || inv?.uid || inv?.userId,
    managerUid: inv?.managerUid || inv?.userUid || inv?.acceptedBy,
    active:
      inv?.active === true ||
      inv?.accessEnabled === true ||
      inv?.enabled === true ||
      (typeof inv?.status === "string" && inv.status.toLowerCase() === "active"),
  };

  const modeRaw = (inv?.accessMode || inv?.delivery || inv?.mode || "").toString().toLowerCase();
  const instant =
    inv?.instant === true ||
    inv?.immediate === true ||
    inv?.instantAccess === true ||
    modeRaw === "instant" ||
    modeRaw === "immediate";
  const timed = modeRaw === "scheduled" || modeRaw === "timed";
  const death = modeRaw === "upon-death" || modeRaw === "upon_death" || modeRaw === "death";

  if (instant) g.accessMode = "instant";
  else if (timed) g.accessMode = "timed";
  else if (death) {
    g.accessMode = "death";
    g.unlockOnDeath = true;
  }

  g.unlockAt = inv?.unlockAt || inv?.deliverAt || inv?.scheduledAt || inv?.unlockOn || null;
  g.accepted = inv?.accepted === true;
  g.immediate = inv?.immediate === true || instant;

  // name/email hints for display fallback
  g.ownerName =
    inv?.ownerName ||
    inv?.ownerFullName ||
    inv?.owner_displayName ||
    inv?.ownerEmail ||
    "";
  return g;
}

function computeCanAccess(grant, ownerProfile) {
  if (!grant) return false;
  if (grant.active === true) return true;
  if (grant.accessEnabled === true || grant.enabled === true) return true;
  if (typeof grant.status === "string" && grant.status.toLowerCase() === "active") return true;
  if (grant.accepted === true && (grant.immediate === true || grant.accessMode === "instant")) return true;

  const unlockAt = grant.unlockAt || grant.unlockAfter || grant.unlockOn;
  if (unlockAt) {
    const ms =
      unlockAt instanceof Timestamp ? unlockAt.toMillis()
      : typeof unlockAt === "number" ? unlockAt
      : typeof unlockAt === "string" ? Date.parse(unlockAt)
      : 0;
    if (ms && Date.now() >= ms) return true;
  }

  if ((grant.unlockOnDeath === true || grant.accessMode === "death") && ownerProfile?.status === "deceased") {
    return true;
  }
  return false;
}

/** Prefer Profile “Full Name” -> name -> displayName -> email -> ownerUid */
function displayOwnerName(owner, grant, ownerUid) {
  return (
    owner?.profile?.fullName ||
    owner?.fullName ||
    owner?.name ||
    owner?.displayName ||
    owner?.email ||
    grant?.ownerName ||
    ownerUid ||
    "—"
  );
}

export default function ManagedAccounts() {
  const debugFlag = useQueryFlag("debug");
  const debug = debugFlag === "1" || debugFlag === "true";

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]); // [{ ownerUid, owner, grant, canAccess }]
  const [error, setError] = useState("");

  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const fetchCF = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) throw new Error("not_signed_in");
    const idToken = await u.getIdToken(true);

    // GET with Bearer
    try {
      const r1 = await fetch("/api/accountManagers/listForManager", {
        method: "GET",
        headers: { Authorization: `Bearer ${idToken}` },
        credentials: "include",
      });
      if (r1.ok) return await r1.json();
      if (debug) console.warn("GET Bearer failed", r1.status, await r1.text().catch(()=>""));
      if (![401,403,405].includes(r1.status)) throw new Error(`GET Bearer ${r1.status}`);
    } catch (e) {
      if (debug) console.warn("GET Bearer error", e);
    }

    // GET with ?id_token=
    try {
      const r2 = await fetch(`/api/accountManagers/listForManager?id_token=${encodeURIComponent(idToken)}`, {
        method: "GET",
        credentials: "include",
      });
      if (r2.ok) return await r2.json();
      if (debug) console.warn("GET ?id_token failed", r2.status, await r2.text().catch(()=>""));
      if (r2.status !== 405) throw new Error(`GET id_token ${r2.status}`);
    } catch (e) {
      if (debug) console.warn("GET id_token error", e);
    }

    // POST fallback
    const r3 = await fetch("/api/accountManagers/listForManager", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      credentials: "include",
      body: "{}",
    });
    if (!r3.ok) {
      const t = await r3.text().catch(()=>"");
      throw new Error(`POST Bearer ${r3.status}: ${t}`);
    }
    return r3.json();
  }, [debug]);

  const fetchViaFirestore = useCallback(async (uid, email) => {
    const rows = [];

    try {
      const q1 = query(collection(db, "grants"), where("managerUid", "==", uid));
      const s1 = await getDocs(q1);
      for (const d of s1.docs) {
        const g = d.data() || {};
        const ownerUid = g.ownerUid;
        if (!ownerUid) continue;
        const ownerSnap = await getDoc(doc(db, "users", ownerUid));
        const owner = ownerSnap.exists() ? ownerSnap.data() : null;
        rows.push({ ownerUid, owner, grant: g });
      }
    } catch (e) {
      if (debug) console.warn("grants fetch failed:", e);
    }

    try {
      const q2 = query(collection(db, "accountManagers"), where("managerUid", "==", uid));
      const s2 = await getDocs(q2);
      for (const d of s2.docs) {
        const inv = d.data() || {};
        const ownerUid = inv.ownerUid || inv.uid || inv.userId;
        if (!ownerUid) continue;
        if (!rows.find((r) => r.ownerUid === ownerUid)) {
          const ownerSnap = await getDoc(doc(db, "users", ownerUid));
          const owner = ownerSnap.exists() ? ownerSnap.data() : null;
          rows.push({ ownerUid, owner, grant: virtualGrantFromInvite(inv) });
        }
      }
    } catch (e) {
      if (debug) console.warn("invites by managerUid failed:", e);
    }

    if (email) {
      try {
        const q3 = query(collection(db, "accountManagers"), where("email", "==", email));
        const s3 = await getDocs(q3);
        for (const d of s3.docs) {
          const inv = d.data() || {};
          const ownerUid = inv.ownerUid || inv.uid || inv.userId;
          if (!ownerUid) continue;
          if (!rows.find((r) => r.ownerUid === ownerUid)) {
            const ownerSnap = await getDoc(doc(db, "users", ownerUid));
            const owner = ownerSnap.exists() ? ownerSnap.data() : null;
            rows.push({ ownerUid, owner, grant: virtualGrantFromInvite(inv) });
          }
        }
      } catch (e) {
        if (debug) console.warn("invites by email failed:", e);
      }
    }

    return rows;
  }, [debug]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const u = auth.currentUser;
        if (!u) {
          setItems([]);
          setError("You must be signed in.");
          return;
        }

        try {
          const payload = await fetchCF();
          if (Array.isArray(payload?.items)) {
            const rows = payload.items;
            const hydrated = [];
            for (const it of rows) {
              const ownerUid = it.ownerUid || it.owner?.uid || it.grant?.ownerUid;
              let owner = it.owner || null;
              if (!owner && ownerUid) {
                const s = await getDoc(doc(db, "users", ownerUid));
                if (s.exists()) owner = s.data();
              }
              const grant = it.grant || it;
              hydrated.push({
                ownerUid,
                owner,
                grant,
                canAccess: computeCanAccess(grant, owner),
              });
            }
            if (alive) setItems(hydrated);
            return;
          }
        } catch (e) {
          if (debug) console.warn("CF failed, fallback to Firestore:", e?.message || e);
        }

        const rows = await fetchViaFirestore(u.uid, u.email || "");
        const hydrated = [];
        for (const r of rows) {
          let owner = r.owner;
          if (!owner && r.ownerUid) {
            const s = await getDoc(doc(db, "users", r.ownerUid));
            if (s.exists()) owner = s.data();
          }
          hydrated.push({
            ...r,
            owner,
            canAccess: computeCanAccess(r.grant, r.owner),
          });
        }
        if (alive) setItems(hydrated);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load managed business accounts.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [fetchCF, fetchViaFirestore, debug]);

  const onAccess = useCallback((ownerUid) => {
    // Mark managed mode so the global banner can show and pages can render read-only cues.
    try { localStorage.setItem("managedOwnerUid", ownerUid); } catch {}
    const url = `/manager?ownerUid=${encodeURIComponent(ownerUid)}&managed=1`;
    window.location.assign(url);
  }, []);

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.canAccess && !b.canAccess) return -1;
      if (!a.canAccess && b.canAccess) return 1;
      const an = displayOwnerName(a.owner, a.grant, a.ownerUid).toLowerCase();
      const bn = displayOwnerName(b.owner, b.grant, b.ownerUid).toLowerCase();
      return an.localeCompare(bn);
    });
  }, [items]);

  const actionsDesktop = (
    <>
      <button className="btn am-help read" onClick={() => setShowDrawer(true)} type="button" data-allow-readonly>
        <FiEye className="am-help-ico" /> Read how it works
      </button>
      <button className="btn am-help see" onClick={() => setShowVideo(true)} type="button" data-allow-readonly>
        <FaYoutube className="am-help-ico" /> See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="am-help-row">
      <button className="btn am-help read" onClick={() => setShowDrawer(true)} type="button" data-allow-readonly>
        <FiEye className="am-help-ico" /> Read how it works
      </button>
      <button className="btn am-help see" onClick={() => setShowVideo(true)} type="button" data-allow-readonly>
        <FaYoutube className="am-help-ico" /> See how it works
      </button>
    </div>
  );

  return (
    <>
      <PageScaffold
        title="Managed Business Accounts"
        heroSrc="/lastwish-account-managers.jpg"
        heroAlt="Managed Business Accounts"
        onAdd={undefined}
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
      >
        {debug && (
          <div style={{ background:"#fff7ed",border:"1px dashed #f59e0b",padding:8,marginBottom:8,borderRadius:8 }}>
            <div><b>Debug</b> items={items.length} loading={String(loading)} error={error || "none"}</div>
          </div>
        )}

        <div className="am-card">
          {loading && <div className="am-empty">Loading…</div>}
          {!loading && error && <div className="am-empty" style={{ color:"#9b1c1c" }}>Error: {error}</div>}
          {!loading && !error && sorted.length === 0 && (
            <div className="am-empty">
              <div style={{ fontWeight:600, marginBottom:6 }}>No managed business accounts yet</div>
              <div className="text-muted">
                After you accept an invitation, the account appears here. If you already accepted,
                access may require owner enablement, a scheduled time, or a death confirmation.
              </div>
            </div>
          )}

          {!loading && !error && sorted.length > 0 && (
            <ul className="am-list">
              {sorted.map(({ ownerUid, owner, grant, canAccess }) => {
                const title = displayOwnerName(owner, grant, ownerUid);
                const info = (() => {
                  if (!grant) return "Business access not yet available";
                  const m = grant.accessMode;
                  if (m === "instant") return "Unlocks: now (instant access)";
                  const unlockAt = grant.unlockAt || grant.unlockAfter || grant.unlockOn;
                  if (unlockAt) {
                    const dt =
                      unlockAt instanceof Timestamp
                        ? new Date(unlockAt.toMillis())
                        : new Date(typeof unlockAt === "number" ? unlockAt : String(unlockAt));
                    if (!isNaN(dt.valueOf())) return `Unlocks on ${dt.toLocaleString()}`;
                  }
                  if ((grant.unlockOnDeath === true || m === "death")) {
                    const status = owner?.status === "deceased" ? "owner marked deceased" : "upon owner’s death";
                    return `Unlocks: ${status}`;
                  }
                  if (grant.accessEnabled === true || grant.enabled === true) return "Unlocks: now (access enabled)";
                  return "Business access not yet available";
                })();

                const mode = (() => {
                  const m = grant?.accessMode;
                  if (m === "instant") return "Instant";
                  if (m === "timed") return "Scheduled";
                  if (m === "death") return "Upon death";
                  return "—";
                })();

                // A grant only exists once amAccept has run, and it carries the
                // manager's real uid. There is no `accepted` field on a grant or
                // on virtualGrantFromInvite, so the old check was always false
                // and every accepted invite still read "Invitation not accepted".
                const isAccepted = !!(grant?.managerUid || grant?.acceptedAt || grant?.accepted);

                const status =
                  canAccess ? "Access enabled"
                  : isAccepted ? (info || "Locked")
                  : "Invitation not accepted";

                return (
                  <li key={ownerUid} className="am-row">
                    {/* LEFT: Owner name (Full Name / email / uid) */}
                    <div className="am-col am-left">
                      <div className="am-name">{title}</div>
                    </div>

                    {/* MIDDLE: chips + status */}
                    <div className="am-col am-mid">
                      <div className="am-sub">
                        <span className="am-chip">Access mode: {mode}</span>
                        {grant?.unlockAt && (
                          <span className="am-chip">
                            {info.replace(/^Unlocks:\s*/, "Unlock: ")}
                          </span>
                        )}
                        {(grant?.readOnly !== false) && <span className="am-chip">Read-only access</span>}
                      </div>
                      <div className={`am-status ${canAccess ? "ok" : "warn"}`}>
                        {status}
                      </div>
                    </div>

                    {/* RIGHT: button */}
                    <div className="am-col am-right">
                      <button
                        type="button"
                        className={`btn ${canAccess ? "btn-main" : "btn-disabled"} am-btn`}
                        disabled={!canAccess}
                        onClick={() => canAccess && onAccess(ownerUid)}
                        title={canAccess ? "Open read-only business view" : info}
                        data-allow-readonly
                      >
                        {canAccess ? "Open account" : "Locked"}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PageScaffold>

      {/* Drawer */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <h3 className="dp-drawer-title">How “Managed Business Accounts” works</h3>
              <div className="dp-drawer-tools">
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button">Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>When a business account owner invites you, the account appears here. Access is read-only and follows the owner’s settings:</p>
              <ul>
                <li><strong>Instant</strong> — the owner enables access immediately.</li>
                <li><strong>Scheduled</strong> — access unlocks on a set date/time.</li>
                <li><strong>Upon death</strong> — access unlocks when the owner is marked deceased.</li>
              </ul>
              <p>Tiles show the owner’s name, access mode and current status. When access is available, press <em>Open account</em>.</p>
              <p>Entering an account sets a temporary “managed mode” flag so your UI can indicate read-only view.</p>
            </div>
          </aside>
        </div>
      )}

      {/* Video lightbox */}
      {showVideo && (
        <VideoLightbox
          open={showVideo}
          onClose={() => setShowVideo(false)}
          url="https://www.youtube.com/watch?v=z5V0Z-hrtSI"
        />
      )}

      {/* Minimal layout CSS plus help buttons */}
      <style>{`
      .am-help-row { display:flex; gap:10px; flex-wrap:wrap; }
        .am-help { border-radius:8px; padding:10px 14px; display:inline-flex; align-items:center; gap:10px; font-weight:700; color:#fff; border:none; }
        .am-help.read { background:#650202; color:#fff; }
        .am-help.see  { background:#003353; color:#fff; }
        .am-help-ico { font-size:1.1rem; }
        .am-card{background:#fff;border:1px solid #ececf5;border-radius:14px;padding:12px}
        .am-empty{padding:16px;color:#6b7280}

        .am-list{list-style:none;margin:0;padding:0;display:grid;gap:12px}
        .am-row{
          display:grid;grid-template-columns:1.2fr 2fr 220px;gap:12px;align-items:center;
          border:1px solid #ececf5;border-radius:12px;padding:12px 14px;background:#fff;
        }
        .am-col{min-width:0}
        .am-left .am-name{font-weight:700;color:#0f172a;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .am-sub{display:flex;flex-wrap:wrap;gap:6px;margin-top:4px}
        .am-chip{font-size:.8rem;background:#f8fafc;border:1px solid #e5e7eb;border-radius:999px;padding:2px 8px;color:#334155}
        .am-status{margin-top:6px;font-size:.92rem}
        .am-status.ok{color:#0e9f6e}
        .am-status.warn{color:#b45309}
        .am-right .am-btn{width:100%}

        .dp-drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,.3);z-index:10000;display:flex;justify-content:flex-end}
        .dp-drawer{position:fixed;top:56px;right:0;height:calc(100vh - 56px);width:min(520px,95%);
          background:#fff;border-left:1px solid #eee;border-top-left-radius:12px;border-bottom-left-radius:12px;
          display:flex;flex-direction:column;overflow:hidden}
        .dp-drawer-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #f0f0f0;background:#fafafa}
        .dp-drawer-title{font-weight:700;color:#2a0516}
        .dp-drawer-tools{display:flex;gap:8px}
        .dp-drawer-body{padding:16px;overflow:auto}

        @media (max-width: 900px){
          .am-row{grid-template-columns:1fr}
          .am-right .am-btn{width:100%}
        }
      `}</style>
    </>
  );
}
